import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BadgeCheck, Loader2, Pencil, Plus } from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { noAutofillProps } from '../../utils/noAutofill';

/**
 * Provider name combobox for the Airport VIP Service form.
 *
 * - Fetches existing org-scoped providers as the user types.
 * - Selecting a provider populates name + emits the full record so the
 *   parent form can auto-fill website/phone/email.
 * - "Edit" opens the parent's edit modal for the currently-selected provider.
 * - Free typing is allowed; the parent will auto-create on form submit.
 */
const VipServiceProviderAutocomplete = ({
  value,
  onChange,
  selectedProvider,
  onProviderSelect,
  onEditClick,
  placeholder = 'e.g. Laufer, Royal Airport Concierge, airssist',
  required = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const fetchProviders = async (query) => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/vip-service-providers`, {
        headers,
        params: query ? { q: query } : {},
      });
      setSuggestions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load VIP service providers', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch as the user types (also prefetch on focus when query is empty).
  useEffect(() => {
    if (!showSuggestions) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProviders(value || '');
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, showSuggestions]);

  // Close the suggestion list when clicking outside the component.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSelect = (provider) => {
    onChange(provider.name || '');
    if (onProviderSelect) onProviderSelect(provider);
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    // If user edits the name away from the linked provider, drop the link
    // so they're not silently mutating a different provider on save.
    if (selectedProvider && newValue !== selectedProvider.name) {
      if (onProviderSelect) onProviderSelect(null);
    }
    setShowSuggestions(true);
  };

  return (
    <div className="space-y-2" ref={wrapperRef}>
      <label
        htmlFor="vip_provider_name"
        className={`text-sm font-semibold text-ui-ink-2 ${required ? 'after:content-["*"] after:text-ui-red-fg after:ml-1' : ''}`}
      >
        Provider Name
      </label>

      <div className="relative">
        <input
          id="vip_provider_name"
          type="text"
          value={value || ''}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          required={required}
          {...noAutofillProps}
          name="vip-provider-search"
          className="w-full h-12 pl-4 pr-24 border border-ui-line rounded-lg focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
        />

        {/* Right-side affordances: status icon + edit pencil for linked provider */}
        <div className="absolute inset-y-0 right-2 flex items-center gap-1">
          {loading && <Loader2 className="w-4 h-4 text-ui-muted animate-spin" />}
          {selectedProvider && (
            <span
              className="inline-flex items-center gap-1 text-xs text-ui-amber-fg bg-ui-amber-bg border border-ui-amber-line px-1.5 py-0.5 rounded"
              title="Linked to a saved provider profile"
            >
              <BadgeCheck className="w-3 h-3" /> Saved
            </span>
          )}
          {selectedProvider && onEditClick && (
            <button
              type="button"
              onClick={() => onEditClick(selectedProvider)}
              className="p-1.5 rounded hover:bg-ui-hover text-ui-muted hover:text-ui-ink-2"
              title="Edit provider profile"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Suggestion dropdown */}
        {showSuggestions && (
          <div className="absolute z-20 mt-1 w-full bg-ui-surface border border-ui-line rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {loading && suggestions.length === 0 && (
              <div className="p-3 text-sm text-ui-muted flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading providers…
              </div>
            )}
            {!loading && suggestions.length === 0 && (
              <div className="p-3 text-sm text-ui-muted flex items-center gap-2">
                <Plus className="w-4 h-4" />
                {value && value.trim()
                  ? <>No matches — &quot;{value.trim()}&quot; will be saved as a new provider on submit.</>
                  : <>Start typing to search saved providers, or type a new name.</>
                }
              </div>
            )}
            {suggestions.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleSelect(p)}
                className="w-full text-left px-3 py-2 hover:bg-ui-amber-bg border-b border-ui-divider last:border-b-0"
              >
                <div className="font-medium text-ui-ink flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-ui-amber-fg" />
                  {p.name}
                </div>
                <div className="text-xs text-ui-muted mt-0.5 space-x-2">
                  {p.website && <span>{p.website}</span>}
                  {p.phone && <span>· {p.phone}</span>}
                  {p.email && <span>· {p.email}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VipServiceProviderAutocomplete;
